import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, Mic, Library, Settings, User, LogIn, 
  Globe, Code, FileText, Smartphone, Monitor,
  ExternalLink, ChevronRight
} from 'lucide-react';

interface RouteSection {
  title: string;
  description: string;
  icon: React.ReactNode;
  routes: {
    path: string;
    label: string;
    description: string;
    external?: boolean;
  }[];
}

export const Sitemap: React.FC = () => {
  const sections: RouteSection[] = [
    {
      title: 'Mobile App Routes',
      description: 'Mobile-first interface with bottom navigation',
      icon: <Smartphone className="w-5 h-5" />,
      routes: [
        { path: '/', label: 'Mobile Home', description: 'Dashboard with stats and recent recordings' },
        { path: '/record', label: 'Record', description: 'Voice recording and AI response generation' },
        { path: '/library', label: 'Library', description: 'View and manage all recordings' },
        { path: '/recordings', label: 'Recordings (alias)', description: 'Alternative path to library' },
        { path: '/settings', label: 'Settings', description: 'App preferences and configuration' },
      ]
    },
    {
      title: 'Desktop Marketing',
      description: 'Landing pages and marketing content',
      icon: <Monitor className="w-5 h-5" />,
      routes: [
        { path: '/welcome', label: 'Landing Page', description: 'Main marketing page with audio demo' },
        { path: '/coach', label: 'AI Coach Demo', description: 'Legacy self-talk interface' },
        { path: '/demo', label: 'Demo (alias)', description: 'Alternative path to coach' },
      ]
    },
    {
      title: 'Authentication',
      description: 'User account management',
      icon: <User className="w-5 h-5" />,
      routes: [
        { path: '/login', label: 'Login', description: 'User sign in' },
        { path: '/signup', label: 'Sign Up', description: 'Create new account' },
        { path: '/auth', label: 'Auth (unified)', description: 'Combined auth page' },
      ]
    },
    {
      title: 'Development Tools',
      description: 'Internal documentation and debugging',
      icon: <Code className="w-5 h-5" />,
      routes: [
        { path: '/app', label: 'Main App', description: 'Legacy app interface' },
        { path: '/architecture', label: 'Architecture', description: 'System design diagram' },
        { path: '/structure', label: 'Project Structure', description: 'Codebase organization' },
        { path: '/sitemap', label: 'Sitemap (this page)', description: 'All available routes' },
        { path: '/analytics', label: 'Analytics Dashboard', description: 'Privacy-first usage insights' },
        { path: '/test', label: 'Test Dashboard', description: 'API and feature testing suite' },
      ]
    },
    {
      title: 'External Links',
      description: 'API endpoints and external services',
      icon: <Globe className="w-5 h-5" />,
      routes: [
        { path: 'http://localhost:8787', label: 'API Server', description: 'Backend API (Cloudflare Workers)', external: true },
        { path: 'http://localhost:3001', label: 'Web App', description: 'Current running instance', external: true },
        { path: 'http://localhost:3005', label: 'Alternative Port', description: 'Fallback dev server', external: true },
      ]
    }
  ];

  const currentUrl = window.location.href;
  const baseUrl = window.location.origin;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-slate-900 to-purple-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Project Sitemap</h1>
          <p className="text-gray-400">All available routes and links in the AI Coach application</p>
          <div className="mt-4 p-3 bg-white/10 rounded-lg">
            <p className="text-sm text-gray-300">
              <span className="font-semibold">Current URL:</span> {currentUrl}
            </p>
            <p className="text-sm text-gray-300">
              <span className="font-semibold">Base URL:</span> {baseUrl}
            </p>
          </div>
        </div>

        {/* Route Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <div key={index} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg text-purple-400">
                  {section.icon}
                </div>
                <div>
                  <h2 className="text-xl font-semibold">{section.title}</h2>
                  <p className="text-sm text-gray-400">{section.description}</p>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {section.routes.map((route, routeIndex) => (
                  <div key={routeIndex}>
                    {route.external ? (
                      <a
                        href={route.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-purple-400">{route.label}</span>
                              <ExternalLink className="w-3 h-3 text-gray-500" />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{route.path}</p>
                            <p className="text-sm text-gray-400 mt-2">{route.description}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-purple-400 transition-colors" />
                        </div>
                      </a>
                    ) : (
                      <Link
                        to={route.path}
                        className="block p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all group"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="font-medium text-purple-400">{route.label}</span>
                            <p className="text-xs text-gray-500 mt-1">{route.path}</p>
                            <p className="text-sm text-gray-400 mt-2">{route.description}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-purple-400 transition-colors" />
                        </div>
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* App Flow Diagram */}
        <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold mb-4">App Flow Structure</h2>
          <pre className="text-xs text-gray-300 overflow-x-auto">
{`Mobile Flow (Primary):
┌─────────────┐
│  / (Home)   │ ← Entry point for mobile users
└──────┬──────┘
       │
┌──────▼──────────────────────┐
│  Bottom Tab Navigation      │
│  ┌──────┬───────┬────────┐ │
│  │ Home │Record │Library │ │
│  └──────┴───────┴────────┘ │
└─────────────────────────────┘

Desktop Flow (Marketing):
┌─────────────┐
│  /welcome   │ ← Landing page
└──────┬──────┘
       │
       ├─→ /login → /app
       ├─→ /signup → /app
       └─→ /coach (demo)

Route Detection:
- Mobile routes: /, /record, /library, /settings
- Desktop routes: /welcome, /coach, /demo, /auth
- Auto-detects device type and adjusts layout`}</pre>
        </div>

        {/* File Structure */}
        <div className="mt-8 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
          <h2 className="text-xl font-semibold mb-4">Key Component Files</h2>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">src/App.tsx</span>
              <span className="text-gray-500">- Main router and layout logic</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">src/components/mobile/MobileLayout.tsx</span>
              <span className="text-gray-500">- Mobile wrapper with nav</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">src/components/mobile/MobileHome.tsx</span>
              <span className="text-gray-500">- Mobile dashboard</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">src/components/MobileRecorder.tsx</span>
              <span className="text-gray-500">- Voice recording interface</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">src/components/RecordingsLibrary.tsx</span>
              <span className="text-gray-500">- Recordings management</span>
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-gray-300">src/components/LandingPageWithAudio.tsx</span>
              <span className="text-gray-500">- Marketing landing</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};